// Time formatting shared by the attendance surfaces — the records table's
// LAST SEEN and punch times, the live-locations rows and the map's pin card.
// Pure functions: `t` comes in as a parameter so tests assert the
// interpolation args instead of a rendered locale string.

import i18n from '@/i18n/config';

type T = (key: string, opts?: Record<string, unknown>) => string;

/** Clock time of an ISO instant, in the active locale ("6:45 AM" / "٦:٤٥ ص"). */
export const fmtClock = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit' }).format(d);
};

/** Whole minutes since an ISO instant, clamped at zero so clock skew can't
 *  render a negative age. */
const minutesSince = (iso: string): number =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));

/** How long ago an ISO instant was — "33 min ago" under an hour, "7h 26m ago"
 *  under a day, "3d 5h 26m ago" beyond. */
export const fmtAgo = (iso: string, t: T): string => {
  const mins = minutesSince(iso);
  if (mins < 60) return t('attendance_min_ago', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('attendance_hr_min_ago', { h: hours, m: mins % 60 });
  return t('attendance_day_hr_min_ago', {
    d: Math.floor(hours / 24),
    h: hours % 24,
    m: mins % 60,
  });
};

/** "Just now" inside the first minute, else the relative age ("33 min ago"). */
export const fmtRelative = (iso: string, t: T): string =>
  minutesSince(iso) < 1 ? t('attendance_just_now') : fmtAgo(iso, t);

/** The pin card's "Last seen" — "Just now · 3:40 PM" inside the first
 *  minute, "33 min ago · 3:07 PM" after. */
export const fmtLastSeen = (iso: string, t: T): string =>
  `${fmtRelative(iso, t)} · ${fmtClock(iso)}`;
